import React, { useEffect, useRef } from "react";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { Alert, Grid, InputLabel, MenuItem, OutlinedInput, Select } from "@mui/material";
import { useConveniosState } from "../context/convenios-context";
import { useConveniosController } from "../context/convenios-controller";
import PlainTextArea from "../../../components/plain-text-area";
import { TextInputWithCounter } from "../../../components/text-input-with-counter";
import { CurrencyInput } from "../../../components/currency-input";
import { ConvenioDocumentosSection } from "./convenio-documentos-section";

export const ConvenioForm = () => {
    const { dataRequest, orgaosList, situacoesList } = useConveniosState();
    const { handleOnChangeForm, loadLookups } = useConveniosController();
    const firstInputRef = useRef<HTMLInputElement>(null);
    const isExistingRecord = dataRequest?.id !== undefined && dataRequest?.id !== null;

    useEffect(() => {
        if (firstInputRef.current) {
            firstInputRef.current.focus();
        }
        (async () => {
            await loadLookups();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <React.Fragment>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={2}>
                    <Grid item lg={12} sm={12} xl={12}>
                        <Alert severity="info">
                            Se o Órgão ainda não existir, cadastre-o primeiro no menu lateral antes de preencher este Convênio.
                        </Alert>
                    </Grid>

                    <Grid item lg={6} sm={6} xl={6}>
                        <InputLabel>Órgão *</InputLabel>
                        <Select
                            size="small"
                            error={!dataRequest?.idOrgao}
                            value={dataRequest?.idOrgao ?? ''}
                            onChange={(e) => handleOnChangeForm('idOrgao', e.target.value)}
                            fullWidth
                        >
                            <MenuItem value={''} key={0}><em>Selecionar</em></MenuItem>
                            {orgaosList?.slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')).map((o) => <MenuItem value={o.id} key={o.id}>{o.name}</MenuItem>)}
                        </Select>
                    </Grid>

                    <Grid item lg={6} sm={6} xl={6}>
                        <InputLabel>Número do Convênio *</InputLabel>
                        <TextInputWithCounter
                            ref={firstInputRef}
                            size="small"
                            maxLength={20}
                            error={!dataRequest?.numeroConvenio}
                            value={dataRequest?.numeroConvenio || ''}
                            onChange={(e) => handleOnChangeForm('numeroConvenio', e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item lg={4} sm={4} xl={4}>
                        <InputLabel>Situação *</InputLabel>
                        <Select
                            size="small"
                            error={!dataRequest?.idSituacaoConvenio}
                            value={dataRequest?.idSituacaoConvenio ?? ''}
                            onChange={(e) => handleOnChangeForm('idSituacaoConvenio', e.target.value)}
                            fullWidth
                        >
                            <MenuItem value={''} key={0}><em>Selecionar</em></MenuItem>
                            {situacoesList?.slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')).map((o) => <MenuItem value={o.id} key={o.id}>{o.name}</MenuItem>)}
                        </Select>
                    </Grid>

                    <Grid item lg={4} sm={4} xl={4}>
                        <InputLabel>Concedente *</InputLabel>
                        <TextInputWithCounter
                            size="small"
                            maxLength={100}
                            error={!dataRequest?.nomConcedente}
                            value={dataRequest?.nomConcedente || ''}
                            onChange={(e) => handleOnChangeForm('nomConcedente', e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item lg={4} sm={4} xl={4}>
                        <InputLabel>Convenente *</InputLabel>
                        <TextInputWithCounter
                            size="small"
                            maxLength={100}
                            error={!dataRequest?.nomConvenente}
                            value={dataRequest?.nomConvenente || ''}
                            onChange={(e) => handleOnChangeForm('nomConvenente', e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item lg={12} sm={12} xl={12}>
                        <InputLabel>Objeto *</InputLabel>
                        <PlainTextArea
                            size="small"
                            rows={4}
                            error={!dataRequest?.textoObjetoConvenio}
                            value={dataRequest?.textoObjetoConvenio || ''}
                            onChange={(e: any) => handleOnChangeForm('textoObjetoConvenio', e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item lg={12} sm={12} xl={12}>
                        <InputLabel>Justificativa</InputLabel>
                        <PlainTextArea
                            size="small"
                            rows={3}
                            value={dataRequest?.textoJustificativaConvenio || ''}
                            onChange={(e: any) => handleOnChangeForm('textoJustificativaConvenio', e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item lg={4} sm={4} xl={4}>
                        <InputLabel>Valor do Convênio *</InputLabel>
                        <CurrencyInput
                            size="small"
                            error={!dataRequest?.valorConvenio || dataRequest.valorConvenio <= 0}
                            value={dataRequest?.valorConvenio}
                            onChange={(value) => handleOnChangeForm('valorConvenio', value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item lg={4} sm={4} xl={4}>
                        <InputLabel>Valor de Contrapartida</InputLabel>
                        <CurrencyInput
                            size="small"
                            value={dataRequest?.valorContrapartida}
                            onChange={(value) => handleOnChangeForm('valorContrapartida', value === 0 ? null : value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item lg={4} sm={4} xl={4}>
                        <InputLabel>Valor Repassado *</InputLabel>
                        <CurrencyInput
                            size="small"
                            error={!dataRequest?.valorRepassado || dataRequest.valorRepassado <= 0}
                            value={dataRequest?.valorRepassado}
                            onChange={(value) => handleOnChangeForm('valorRepassado', value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item lg={3} sm={3} xl={3}>
                        <InputLabel>Data de Celebração *</InputLabel>
                        <DesktopDatePicker
                            format='DD/MM/YYYY'
                            slotProps={{ textField: { fullWidth: true, size: "small", error: !dataRequest?.dtaCelebracao } }}
                            value={dataRequest?.dtaCelebracao ? dayjs(dataRequest.dtaCelebracao) : null}
                            onChange={(value: Dayjs | null) => handleOnChangeForm('dtaCelebracao', value?.toISOString())}
                        />
                    </Grid>

                    <Grid item lg={3} sm={3} xl={3}>
                        <InputLabel>Início da Vigência *</InputLabel>
                        <DesktopDatePicker
                            format='DD/MM/YYYY'
                            slotProps={{ textField: { fullWidth: true, size: "small", error: !dataRequest?.dtaInicioVigencia } }}
                            value={dataRequest?.dtaInicioVigencia ? dayjs(dataRequest.dtaInicioVigencia) : null}
                            onChange={(value: Dayjs | null) => handleOnChangeForm('dtaInicioVigencia', value?.toISOString())}
                        />
                    </Grid>

                    <Grid item lg={3} sm={3} xl={3}>
                        <InputLabel>Fim da Vigência *</InputLabel>
                        <DesktopDatePicker
                            format='DD/MM/YYYY'
                            slotProps={{ textField: { fullWidth: true, size: "small", error: !dataRequest?.dtaFimVigencia } }}
                            value={dataRequest?.dtaFimVigencia ? dayjs(dataRequest.dtaFimVigencia) : null}
                            onChange={(value: Dayjs | null) => handleOnChangeForm('dtaFimVigencia', value?.toISOString())}
                        />
                    </Grid>

                    <Grid item lg={3} sm={3} xl={3}>
                        <InputLabel>Data de Publicação *</InputLabel>
                        <DesktopDatePicker
                            format='DD/MM/YYYY'
                            slotProps={{ textField: { fullWidth: true, size: "small", error: !dataRequest?.dtaPublicacao } }}
                            value={dataRequest?.dtaPublicacao ? dayjs(dataRequest.dtaPublicacao) : null}
                            onChange={(value: Dayjs | null) => handleOnChangeForm('dtaPublicacao', value?.toISOString())}
                        />
                    </Grid>

                    {isExistingRecord ? (
                        <ConvenioDocumentosSection />
                    ) : (
                        <Grid item lg={12} sm={12} xl={12}>
                            <Alert severity="info">
                                Salve o convênio primeiro para liberar o cadastro de Documentos.
                            </Alert>
                        </Grid>
                    )}
                </Grid>
            </LocalizationProvider>
        </React.Fragment>
    );
};
